// Auth context + Supabase client singleton.
// Supabase is initialised lazily after config is fetched from the CF worker
// at /config.json — keys are never baked into the JS bundle.

import { createContext, useContext } from 'react';
import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import { getConfig } from './config';

// ── Types ──────────────────────────────────────────────────────────────────

export interface AuthContextValue {
  readonly user: User | null;
  readonly session: Session | null;
  readonly token: string | null;
  /**
   * The company this session acts on. For a normal user it's their own; for a
   * superadmin it's the company they selected, resolved SERVER-side from the
   * active "view as" row — never from their profile, which may carry a stale
   * legacy company_id.
   */
  readonly companyId: string | null;
  readonly role: string | null;
  readonly loading: boolean;
  /** Name of the company a superadmin is currently acting on (banner label). */
  readonly actingCompanyName: string | null;
  readonly isSuperadmin: boolean;
  readonly signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  readonly signOut: () => Promise<void>;
  /** Superadmin: start acting on a company. Server records + audits the choice. */
  readonly selectCompany: (companyId: string) => Promise<{ error: string | null }>;
  /** Superadmin: stop acting on the current company (server deletes the row). */
  readonly exitCompany: () => Promise<void>;
}

// ── Context ────────────────────────────────────────────────────────────────

export const AuthContext = createContext<AuthContextValue | null>(null);

// ── Supabase client singleton ──────────────────────────────────────────────
// The Promise is cached (not the resolved value) to prevent a race condition:
// if two callers hit getSupabaseClient() concurrently before the first resolves,
// both would pass the null check and call createClient() twice — producing the
// "Multiple GoTrueClient instances" warning and undefined session behaviour.
// Caching the Promise guarantees createClient() is called exactly once.

let supabaseClientPromise: Promise<SupabaseClient> | null = null;

// Plain localStorage persistence. There used to be a custom storage adapter,
// a per-tab handoff flag and a load-time reconciler here — all of it existed
// because "View as Company" minted the CUSTOMER's JWT into this origin, which
// Supabase then broadcast to every other tab. A superadmin now signs in as
// themselves and the company they are acting on lives in a server-side row, so
// there is no foreign session to route, quarantine or purge. Do not reintroduce
// storage tricks: if a session ever needs isolating again, the bug is that a
// credential is being shared, not that it is being stored.
export function getSupabaseClient(): Promise<SupabaseClient> {
  if (supabaseClientPromise !== null) return supabaseClientPromise;
  supabaseClientPromise = getConfig().then(({ supabaseUrl, supabaseAnonKey }) =>
    createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    }),
  );
  // If init fails (e.g. secret not set), clear the cache so next call retries
  supabaseClientPromise.catch(() => { supabaseClientPromise = null; });
  return supabaseClientPromise;
}

// ── Token cache ────────────────────────────────────────────────────────────
// Kept in a module-level variable so non-component code (apiFetch, hooks)
// can read the JWT synchronously instead of awaiting getSession() on every call.

let _cachedToken: string | null = null;

export function setCachedToken(token: string | null): void {
  _cachedToken = token;
}

export function getCachedToken(): string | null {
  return _cachedToken;
}

/**
 * Get a fresh JWT from the Supabase session.
 * Supabase auto-refreshes before expiry so this always returns a valid token.
 * Use this instead of getCachedToken() for long-lived hooks/contexts where
 * the cached token may have gone stale.
 */
export async function getFreshToken(): Promise<string | null> {
  try {
    const supabase = await getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return _cachedToken;
  }
}

// ── Global unauthenticated handler ────────────────────────────────────────
// Registered by AuthProvider so non-component code (apiFetch) can trigger
// a full sign-out + redirect to login when the API returns 401.

let _forceSignOutHandler: (() => void) | null = null;

export function registerForceSignOut(handler: () => void): void {
  _forceSignOutHandler = handler;
}

export function forceSignOut(): void {
  _forceSignOutHandler?.();
}

// ── useAuth hook ───────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// Eagerly kick off the config fetch + Supabase client creation at module load
// time. By the time AuthProvider mounts and calls getSupabaseClient(), the
// Promise is already resolving (or resolved) instead of starting cold.
void getSupabaseClient();
